/**
 * Module for the "Parallax" section.
 */



// Module imports
import { h, render, Component } from 'preact';
import { Utility } from 'utility/utility.js';
import * as Zenscroll from 'zenscroll/zenscroll-min.js';



/**
 * Module constants.
 */
export const FULL_SCREENHEIGHT_CLASSNAME = 'full-screen-height';
export const PARALLAX_TYPE_CSS           = 'css-parallax';
export const PARALLAX_TYPE_CUSTOM_JS     = 'custom-js-parallax';



/**
 * Component for parallax component.
 */
export class ParallaxComponent extends Component
{
	/**
	 * @brief      Run stuff before component mounts.
	 */
	componentWillMount()
	{
		// If it's a traditional desktop OS (but NOT MacOS as the framework that gets the data has started identifying iPads as MacOS)
		if
		(
			'windows' === document.body.getAttribute( 'data-os-slug' )
			||
			'linux' === document.body.getAttribute( 'data-os-slug' )
		)
		{
			// Use a purebred CSS parallax
			this.parallaxType = PARALLAX_TYPE_CSS;
		}
		// Not a traditional desktop OS
		else
		{
			// Use a JS-based parallax
			this.parallaxType = PARALLAX_TYPE_CUSTOM_JS;
		}
	}



	/**
	 * @brief      Run stuff when component has mounted.
	 */
	componentDidMount()
	{
		// If it's the correct parallax type (we wrap the listeners in the if case instead of the other way around so that we don't run the anonymous function and check this on every scroll event and every resize event)
		if ( PARALLAX_TYPE_CUSTOM_JS === this.parallaxType )
		{
			// Set the image position
			this.setImageOffset();

			// Listen for window scrolling
			window.addEventListener(
				'scroll',
				() =>
				{
					// Set the image position
					window.requestAnimationFrame(
						() =>
						{
							this.setImageOffset();
						}
					);
				}
			);

			// Listen for window scrolling
			window.addEventListener(
				'resize',
				() =>
				{
					// Set the image position
					window.requestAnimationFrame(
						() =>
						{
							this.setImageOffset();
						}
					);
				}
			);
		}
	}



	/**
	 * @brief      Sets the image position.
	 */
	setImageOffset()
	{
		// Get offsets
		let scrolledPixels  = window.pageYOffset || document.documentElement.scrollTop;
		let elementPosition = $( this.innerWrap ).offset().top;

		// Set component state
		this.setState(
			{
				imageOffset      : ( scrolledPixels - elementPosition ),
				positionerOffset : ( elementPosition - scrolledPixels )
			}
		);
	}



	/**
	 * Scrolls past the current element.
	 *
	 * @param      evt   The initiating event
	 */
	scrollPast( evt )
	{
		// Reference next element
		let $nextElement = $( evt.target ).closest( 'section' ).next( '*' );

		// Make sure there is a section after this
		if ( 0 < $nextElement.length )
		{
			// Scroll past the element, but take into consideration the menu height
			Zenscroll.toY(
				$nextElement.position().top
			);
		}
	}



	/**
	 * Render the component.
	 *
	 * @param      bgImage     The background image
	 * @param      bgImageAlt  The background image alt text
	 * @param      imageSize   The image size
	 *
	 * @return     Rendered component
	 */
	render( { bgImage, bgImageAlt, imageSize }, { imageOffset = 0, positionerOffset = 0 } )
	{
		// Instantiate scroll past button so we can insert it if necessary
		let scrollPastButton;

		// Only render anything if it's a fullscreen image
		if ( FULL_SCREENHEIGHT_CLASSNAME === imageSize )
		{
			scrollPastButton =
			(
				<button class="parallax__scroll-past" onClick={ evt => this.scrollPast( evt ) }>
					<svg fill="#fff" height="24" viewBox="0 0 24 20" width="24" xmlns="http://www.w3.org/2000/svg">
					    <path d="M7.41 7.84L12 12.42l4.59-4.58L18 9.25l-6 6-6-6z"/>
					    <path d="M0-.75h24v24H0z" fill="none"/>
					</svg>
				</button>
			);
		}

		// What type of Parallax section did we want?
		switch ( this.parallaxType )
		{
			// Our own custom JS variant
			case PARALLAX_TYPE_CUSTOM_JS:
			{
				return (
					<div
					 class={ `parallax__inner ${ imageSize } ${ this.parallaxType }` }
					 ref={ c => this.innerWrap = c }
					>
						<div
						 class={ `parallax__positioner` }
						 style={
						 	`
						 	-webkit-transform: translate3d( 0px, ${ positionerOffset }px, 0px );
						 	transform: translate3d( 0px, ${ positionerOffset }px, 0px );
						 	`
						 }
						>
							<div
							 class={ `parallax__image ${ imageSize }` }
							 style={
							 	`
							 	background-image: url( '${ bgImage }' );
							 	-webkit-transform: translateY( ${ imageOffset }px ) translateZ( 0px );
							 	transform: translateY( ${ imageOffset }px ) translateZ( 0px );
							 	`
							 }
							>
							</div>
						</div>
						<div class={ `parallax__content-wrap` }>
							{ this.props.children }
						</div>
						{ scrollPastButton }
					</div>
				);

				// Super superfluous (lol) break for code clarity
				break;
			}

			// CSS based
			case PARALLAX_TYPE_CSS:
			default:
			{
				return (
					<>
						<div
						 class="parallax__background"
						 style={
						 	`
						 	background-image: url( '${ bgImage }' );
						 	`
						 }
						>
						</div>

						<div class={ `parallax__inner ${ imageSize } ${ this.parallaxType }` }>
							<div class={ `parallax__content-wrap` }>
								{ this.props.children }
							</div>
							{ scrollPastButton }
						</div>
					</>
				);

				// Super superfluous (lol) break for code clarity
				break;
			}
		}
	}
}



/**
 * Class for the "Parallax" section.
 */
export class ParallaxClass
{
	/**
	 * Adds the necessary event listeners.
	 */
	static addListeners()
	{
		Utility.awaitDOMContentLoaded(
			() =>
			{
				// Instantiate sections immediately
				ParallaxClass.instantiateSections();
			}
		);
	}



	/**
	 * Instantiates all parallax sections.
	 */
	static instantiateSections()
	{
		// Find all parallax sections
		let parallaxSections = document.querySelectorAll( 'section.parallax' );

		// If there are any sections
		if ( 0 < parallaxSections.length )
		{
			// Loop sections
			for ( let i = 0; i < parallaxSections.length; i++ )
			{
				// Create an instance for the section
				let ParallaxInstance = new ParallaxClass(
					{
						element : parallaxSections[ i ],
						index   : i,
					}
				);

				// Render everything
				ParallaxInstance.renderSection();
			}
		}
	}



	/**
	 * Class constructor.
	 */
	constructor( args = {} )
	{
		// Default arguments
		args = Object.assign(
			{
				element : undefined,
				index   : 0,
			},
			args
		);

		// Member variables
		this.element = args.element;
		this.fields  = JSON.parse( this.element.getAttribute( 'data-fields' ) );
		this.index   = args.index;
	}



	/**
	 * Renders the actual content for the current element.
	 */
	renderSection()
	{
		// Setup variables for conditional stuff
		let title;
		let subtitle;

		// Do we have a title to use?
		if ( 0 < this.fields.title.length )
		{
			// Is it the first section of it's kind? (NOTE: this assumes that no other sections have H1's)
			if ( 0 === this.index )
			{
				// Then we actually want an H1
				title = <h1 class="parallax__heading" style={ { color: this.fields.titleColor, } }>{ this.fields.title }</h1>;
			}
			// Not the first section
			else
			{
				// Assume we want an H2
				title = <h2 class="parallax__heading" style={ { color: this.fields.titleColor, } }>{ this.fields.title }</h2>;
			}
		}

		// Do we have a subtitle to use?
		if ( 0 < this.fields.subtitle.length )
		{
			subtitle = <span class="parallax__subheading" style={ { color: this.fields.subtitleColor, } }>{ this.fields.subtitle }</span>;
		}

		// Render the actual content
		render(
			(
				<ParallaxComponent
				 bgImage={ this.fields.image }
				 bgImageAlt={ this.fields.title }
				 imageSize={ this.fields.imageSize }
				>
					{ title }
					<div class="parallax__title-divider" aria-hidden="true"></div>
					{ subtitle }
				</ParallaxComponent>
			),
			this.element
		);
	}
}



// WEBPACK FOOTER //
// ../wp-content/themes/wisesite-child/assets/js/modules/parallax/parallax.jsx